// CELL-BIO9 — story executor. Loads JSON nodes (glob-imported at build time),
// applies on_enter, prints lines, gates choices by requirements, applies
// choice effects/flags, autosaves on every node transition, and triggers
// AI scenes when a node declares ai_scene.

import { gameState } from "./gameState";
import { autosave } from "./saveManager";
import { Terminal } from "./terminal";
import { aiClient } from "./aiClient";
import { audioEngine } from "./audio";
import type { Choice, StoryNode } from "./types";
import { renderChoices, clearChoices } from "../ui/choices";

// Every story JSON file may contain a single node or an array of nodes.
const storyModules = import.meta.glob<{ default: StoryNode | StoryNode[] }>(
  "/story/**/*.json",
  { eager: true }
);

function buildRegistry(): Map<string, StoryNode> {
  const registry = new Map<string, StoryNode>();
  for (const [path, mod] of Object.entries(storyModules)) {
    const payload = mod.default;
    const nodes = Array.isArray(payload) ? payload : [payload];
    for (const node of nodes) {
      if (registry.has(node.id)) {
        throw new Error(`Duplicate story node id "${node.id}" (in ${path})`);
      }
      registry.set(node.id, node);
    }
  }
  return registry;
}

/** Previous heat level — used to detect threshold crossings for audio stings. */
let prevHeat = 0;

export class StoryManager {
  private nodes: Map<string, StoryNode>;
  private terminal: Terminal;
  private choicesRoot: HTMLElement;

  constructor(terminal: Terminal, choicesRoot: HTMLElement) {
    this.terminal = terminal;
    this.choicesRoot = choicesRoot;
    this.nodes = buildRegistry();
    prevHeat = gameState.heat;
  }

  get nodeCount(): number {
    return this.nodes.size;
  }

  has(id: string): boolean {
    return this.nodes.has(id);
  }

  async goto(id: string): Promise<void> {
    const node = this.nodes.get(id);
    if (!node) {
      this.terminal.printInstant(`[SYSTEM FAULT] Unknown node: ${id}`, "warning");
      return;
    }

    clearChoices(this.choicesRoot);

    // on_enter effects
    if (node.on_enter?.flags_add) gameState.addFlags(node.on_enter.flags_add);
    if (node.on_enter?.day_advance) {
      gameState.advanceDay();
      audioEngine.dayStingAdvance();
    }

    // Node transition = autosave point.
    gameState.setCurrentNode(node.id);
    autosave(gameState.snapshot());

    // Heat threshold audio stings.
    const currentHeat = gameState.heat;
    if (
      (prevHeat < 40 && currentHeat >= 40) ||
      (prevHeat < 60 && currentHeat >= 60) ||
      (prevHeat < 80 && currentHeat >= 80)
    ) {
      audioEngine.heatStingWarning();
    }
    prevHeat = currentHeat;

    this.terminal.printLines(node.lines);
    await this.terminal.whenIdle();

    // AI scene augmentation (non-blocking — choices appear after AI finishes).
    if (node.ai_scene) {
      await this.runAIScene(node.ai_scene);
    }

    const available = node.choices.filter((c) => gameState.meets(c.requires));
    if (available.length === 0) {
      // Terminal node — end of authored content for this slice.
      audioEngine.endStingCliffhanger();
      this.terminal.printInstant("— END OF CHAPTER 1 DEMO — refresh to run it back —", "system");
      return;
    }

    renderChoices(this.choicesRoot, available, (choice) => {
      void this.pick(choice);
    });
  }

  private async runAIScene(sceneId: string): Promise<void> {
    // Show a freeform input prompt for the player.
    const inputLine = this.terminal.printInstant(
      "[ TYPE YOUR RESPONSE — press ENTER to send, or press any choice key to skip ]",
      "system"
    );
    void inputLine; // used for side-effect only

    const playerInput = await this.promptPlayerInput();
    if (!playerInput) {
      // Player skipped — use fallback silently.
      await aiClient.runScene({ scene: sceneId, playerInput: "(silence)" }, this.terminal);
      return;
    }

    this.terminal.print({ text: `> ${playerInput}`, class: "player" });
    await aiClient.runScene({ scene: sceneId, playerInput }, this.terminal);
    await this.terminal.whenIdle();
  }

  private promptPlayerInput(): Promise<string> {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "text";
      input.className = "ai-input";
      input.maxLength = 500;
      input.placeholder = "...";
      input.setAttribute("aria-label", "Your response");
      this.choicesRoot.appendChild(input);
      input.focus();

      const finish = (value: string): void => {
        input.remove();
        resolve(value.trim());
      };

      input.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          finish(input.value);
        }
        // Number keys 1–4 skip the AI input.
        const n = Number(ev.key);
        if (Number.isInteger(n) && n >= 1 && n <= 4) {
          finish("");
        }
      });

      // Escape also skips.
      input.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape") finish("");
      });
    });
  }

  private async pick(choice: Choice): Promise<void> {
    clearChoices(this.choicesRoot);
    this.terminal.print({ text: `> ${choice.label}`, class: "player" });
    await this.terminal.whenIdle();

    gameState.applyEffects(choice.effects);
    gameState.addFlags(choice.flags_add);
    gameState.removeFlags(choice.flags_remove);

    await this.goto(choice.next);
  }
}
